import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";

const MAX_KB = 100;

export default function FileUploader({ folder = "misc", onUploaded, label = "Upload", multiple = false, testId = "file-upload", maxKb = MAX_KB, accept = "image/*,application/pdf" }) {
  const [busy, setBusy] = useState(false);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    // Client-side size guard so users see the error before wasting bandwidth
    for (const file of files) {
      if (file.size > maxKb * 1024) {
        toast.error(`${file.name} is ${(file.size / 1024).toFixed(0)} KB — please keep files under ${maxKb} KB`);
        return;
      }
    }
    setBusy(true);
    const urls = [];
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post(`/upload?folder=${folder}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        const backend = process.env.REACT_APP_BACKEND_URL;
        urls.push(`${backend}${data.url}`);
      }
      onUploaded(multiple ? urls : urls[0]);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <label className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 rounded cursor-pointer hover:bg-slate-50 text-sm" data-testid={testId}>
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
      {busy ? "Uploading…" : label}
      <span className="text-[10px] text-slate-400">(max {maxKb} KB)</span>
      <input type="file" className="hidden" multiple={multiple} accept={accept}
        onChange={(e) => handleFiles(Array.from(e.target.files || []))} disabled={busy} />
    </label>
  );
}
